// ============================================================================
// DOCUMENT BLOB STORE — durable local backing for reference-document binaries
// ----------------------------------------------------------------------------
// A direct mirror of photo-blob-store.ts (photos) / video-blob-store (videos),
// for reference documents (PDF / DOCX / etc.) shown in the stage Cockpits.
//
// The bug this closes: a reference PDF whose S3 upload failed (auth / rate-limit
// / offline) had its inline base64 stripped on save with no durable fallback,
// and a DOCX never got its bytes stored at all — so on reload the preview was
// gone (and, via the folder-reconnect path, whole rows could vanish). Photos
// already avoid this by migrating un-backed inline bytes into a dedicated Dexie
// table before the strip; this gives documents the same safety net.
//
// Cycle: on save, migrateProjectDocuments() moves un-backed inline bytes into
// `document_blobs` (Dexie v30) and stamps a `docBlobId` on the doc metadata, so
// the payload strip is byte-loss-free. On load, rehydrateProjectDocuments()
// rebuilds a `pdfDataUrl` object URL from the stored blob so the Cockpit can
// preview it again. Bytes live in IndexedDB, not the project JSONB, so the
// record stays small (no 413).
// ============================================================================

import db from './db';
import {
    omitCanonicalBlobFields,
    requireCanonicalProjectUuid,
} from './project-media-write-boundary';

// Track active object URLs with their resolved owner so even a legacy caller
// that omitted expectedProjectId can still be revoked by an exact project
// delete (mirror photo-blob-store's owner-aware cache).
const activeUrls = new Map<string, { url: string; projectId: string | null }>();
// Coalesce identical reads. Without this, two Cockpit/rehydration consumers
// can both miss the cache, mint separate URLs, and overwrite the same cache
// key; the first URL then has no owner left to revoke it.
const pendingUrlLoads = new Map<string, Promise<string | null>>();
// Stage 1 also mints immediate PDF preview URLs before a document has reached
// IndexedDB. Keep those session URLs in this module-level, project-aware
// registry so they survive Stage 1 -> later-stage unmounts but can still be
// released by the central project-switch/reset boundary.
const PENDING_DOCUMENT_PROJECT = Symbol('pending-document-project');
const sessionUrlsByProject = new Map<string | symbol, Set<string>>();
const sessionOwnersByUrl = new Map<string, Set<string | symbol>>();
// A project UUID is retired when deleted and must never be reused. Keep a
// session tombstone so an IndexedDB read that began before deletion cannot
// mint/cache a fresh URL after the delete sweep and revocation have completed.
const deletedProjectIds = new Set<string>();

// A document can be captured before the first Project save has minted its real
// UUID. Those rows carry a sentinel owner and are eligible for a one-time,
// narrowly-scoped claim by the Project record that references them. A row
// owned by any real Project is never re-parented.
const SENTINEL_OWNERS = new Set(['pending', 'unknown', '', 'null', 'undefined']);

function isSentinelOwner(owner: unknown): boolean {
    return owner == null || SENTINEL_OWNERS.has(String(owner));
}

export interface DocumentBlobReferenceInspection {
    ownedIds: number[];
    sentinelIds: number[];
    foreignRefs: Array<{ id: number; ownerProjectId: string }>;
    missingIds: number[];
}

function activeDocumentUrlKey(blobId: any, expectedProjectId: any): string {
    return `${expectedProjectId == null ? '*' : String(expectedProjectId)}\u0000${String(blobId)}`;
}

function documentProjectKey(projectId: any): string | symbol {
    return projectId == null || projectId === '' ? PENDING_DOCUMENT_PROJECT : String(projectId);
}

/** Register a Stage 1 session PDF URL without coupling its lifetime to the component mount. */
export function registerProjectDocumentUrl(projectId: any, url: any) {
    if (typeof url !== 'string' || !url.startsWith('blob:')) return url;
    const owner = documentProjectKey(projectId);
    const projectUrls = sessionUrlsByProject.get(owner) ?? new Set<string>();
    projectUrls.add(url);
    sessionUrlsByProject.set(owner, projectUrls);
    const owners = sessionOwnersByUrl.get(url) ?? new Set<string | symbol>();
    owners.add(owner);
    sessionOwnersByUrl.set(url, owners);
    return url;
}

/** Release one project's Stage 1 PDF URL, preserving another live owner. */
export function releaseProjectDocumentUrl(projectId: any, url: any): boolean {
    if (typeof url !== 'string' || !url.startsWith('blob:')) return false;
    const owner = documentProjectKey(projectId);
    const projectKey = owner === PENDING_DOCUMENT_PROJECT ? null : String(projectId);
    const owners = sessionOwnersByUrl.get(url);
    let owned = false;
    if (owners?.has(owner)) {
        owners.delete(owner);
        owned = true;
    }
    const projectUrls = sessionUrlsByProject.get(owner);
    projectUrls?.delete(url);
    if (projectUrls?.size === 0) sessionUrlsByProject.delete(owner);
    for (const [cacheKey, active] of [...activeUrls.entries()]) {
        if (active.url !== url || active.projectId !== projectKey) continue;
        activeUrls.delete(cacheKey);
        owned = true;
    }
    if (!owned) return false;
    if (owners && owners.size > 0) return false;
    if (owners) sessionOwnersByUrl.delete(url);
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
    return true;
}

/** Store a document Blob in the dedicated document_blobs table. */
export async function storeDocumentBlob(blob: any, projectId: any, metadata: any = {}) {
    if (!(blob instanceof Blob)) {
        throw new Error('storeDocumentBlob requires a Blob instance');
    }
    const canonicalProjectId = requireCanonicalProjectUuid(projectId, 'storeDocumentBlob');
    const metadataMimeType = typeof metadata?.mimeType === 'string' ? metadata.mimeType : '';
    const id = await (db as any).document_blobs.add({
        ...omitCanonicalBlobFields(metadata),
        blob,
        projectId: canonicalProjectId,
        sizeBytes: blob.size,
        mimeType: blob.type || metadataMimeType || 'application/octet-stream',
        storedAt: new Date().toISOString(),
    });
    return id;
}

/**
 * Classify browser-local document references without resolving their bytes or
 * mutating ownership. A missing id means only that this Chrome profile has no
 * matching row; a present row with another real Project owner is foreign.
 */
export async function inspectDocumentBlobReferences(
    blobIds: Array<number | null | undefined>,
    projectId: string,
): Promise<DocumentBlobReferenceInspection> {
    const result: DocumentBlobReferenceInspection = {
        ownedIds: [],
        sentinelIds: [],
        foreignRefs: [],
        missingIds: [],
    };
    if (!projectId || isSentinelOwner(projectId) || !Array.isArray(blobIds)) return result;

    const uniqueIds = [...new Set(blobIds.filter((id): id is number => (
        typeof id === 'number' && Number.isFinite(id)
    )))];
    for (const id of uniqueIds) {
        const record = await (db as any).document_blobs.get(id);
        if (!record) {
            result.missingIds.push(id);
        } else if (String(record.projectId ?? '') === String(projectId)) {
            result.ownedIds.push(id);
        } else if (isSentinelOwner(record.projectId)) {
            result.sentinelIds.push(id);
        } else {
            result.foreignRefs.push({ id, ownerProjectId: String(record.projectId) });
        }
    }
    return result;
}

/** Legacy compatibility API; unsafe integer-only sentinel adoption is disabled. */
export async function claimSentinelDocumentBlobs(
    _blobIds: Array<number | null | undefined>,
    _projectId: string,
): Promise<number[]> {
    return [];
}

export interface DocumentBlobQuarantineResult {
    documents: any[];
    changed: boolean;
    claimedIds: number[];
    foreignRefs: Array<{ id: number; ownerProjectId: string }>;
    missingIds: number[];
}

/**
 * Validate the device-local locator carried by each loaded document.
 *
 * Only a row already owned by this exact Project is trusted. Sentinel rows are
 * unproven: a historical cloud/cache integer can collide with an unrelated
 * pending import after a browser reset. Extracted text, identity, labels,
 * audit metadata and cloud/file receipts remain byte-for-byte intact.
 */
export async function quarantineInvalidDocumentBlobReferences(
    projectId: string,
    documents: any[],
): Promise<DocumentBlobQuarantineResult> {
    const source = Array.isArray(documents) ? documents : [];
    const blobIds = source.flatMap(document => (
        document
        && typeof document === 'object'
        && typeof document.docBlobId === 'number'
        && Number.isFinite(document.docBlobId)
            ? [document.docBlobId]
            : []
    ));
    const inspection = await inspectDocumentBlobReferences(blobIds, projectId);
    const claimedIds: number[] = [];
    const foreignIds = new Set(inspection.foreignRefs.map(ref => ref.id));
    const missingIds = new Set(inspection.missingIds);
    const invalidIds = new Set([...inspection.sentinelIds, ...foreignIds, ...missingIds]);
    if (invalidIds.size === 0) {
        return {
            documents: source,
            changed: false,
            claimedIds,
            foreignRefs: inspection.foreignRefs,
            missingIds: inspection.missingIds,
        };
    }

    let changed = false;
    const cleaned = source.map(document => {
        if (!document || typeof document !== 'object' || !invalidIds.has(document.docBlobId)) {
            return document;
        }
        const next = { ...document };
        delete next.docBlobId;
        if (isBlobUrl(next.pdfDataUrl)) delete next.pdfDataUrl;
        // These are derived browser-cache claims, not customer metadata. Once
        // their exact locator is invalid they must not continue to advertise a
        // local durable copy to later save/recovery decisions.
        delete next._localBlobOnly;
        delete next._localMemoryOnly;
        changed = true;
        return next;
    });
    return {
        documents: changed ? cleaned : source,
        changed,
        claimedIds,
        foreignRefs: inspection.foreignRefs,
        missingIds: inspection.missingIds,
    };
}

/** Retrieve a document Blob by its id, refusing cross-project resolution. */
export async function getDocumentBlob(blobId: any, expectedProjectId: any = null) {
    if (!blobId) return null;
    const record = await (db as any).document_blobs.get(blobId);
    if (!record) return null;
    // INTEGRITY GUARD (mirror photo-blob-store): document_blobs `id` is a global
    // Dexie auto-increment, so a doc carrying another project's docBlobId (e.g. a
    // Duplicate that didn't clone its blob rows) would otherwise resolve the wrong
    // project's bytes. When the caller names the owner, refuse a foreign blob.
    if (expectedProjectId != null
        && String(record.projectId ?? '') !== String(expectedProjectId)) {
        console.warn(`[document-blob-store] Refused cross-project blob ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
        return null;
    }
    return record.blob || null;
}

/**
 * Clone browser-local document blobs for a duplicated project.
 *
 * IndexedDB ids are global and project-owned, so copying a source docBlobId
 * into a new project creates a foreign reference. Clone each distinct local
 * row once, remap every matching document, and discard only the source-bound
 * Blob preview URL. Cloud/file references remain shared metadata and are not
 * uploaded or copied here.
 */
export async function cloneProjectDocumentBlobs(
    documents: any,
    sourceProjectId: any,
    targetProjectId: any,
) {
    if (!Array.isArray(documents) || !sourceProjectId || !targetProjectId) {
        return Array.isArray(documents) ? documents : [];
    }
    const clonedIds = new Map<string, any>();
    const createdTargetBlobIds: any[] = [];
    const out: any[] = [];
    try {
        for (const document of documents) {
            if (!document || typeof document !== 'object') {
                out.push(document);
                continue;
            }
            const sourceBlobId = document.docBlobId;
            let targetBlobId: any = null;
            if (sourceBlobId) {
                const cloneKey = String(sourceBlobId);
                if (clonedIds.has(cloneKey)) {
                    targetBlobId = clonedIds.get(cloneKey);
                } else {
                    const sourceBlob = await getDocumentBlob(sourceBlobId, sourceProjectId);
                    if (sourceBlob) {
                        targetBlobId = await storeDocumentBlob(sourceBlob, targetProjectId, {
                            name: document.name || document.originalName,
                            mimeType: sourceBlob.type || document.mimeType || document.contentType,
                        });
                        createdTargetBlobIds.push(targetBlobId);
                    }
                    clonedIds.set(cloneKey, targetBlobId);
                }
            }

            // Once the bytes have a target-owned row, do not also retain a File,
            // data URL, handle, or source preview in the duplicate's live record.
            // If the source row is unavailable, keep recoverable transient bytes
            // and drop only the invalid foreign id/source Blob URL below.
            const next = targetBlobId
                ? stripTransientDocumentFields(document)
                : { ...document };
            if (sourceBlobId) {
                if (targetBlobId) next.docBlobId = targetBlobId;
                else {
                    const hasPortableCloudReceipt = !!(
                        document.fileMetadataId
                        || document.pdfS3Key
                        || document.s3Key
                        || document.fileS3Key
                        || document.cloudObjectKey
                    );
                    if (!hasPortableCloudReceipt) {
                        const error: any = new Error(
                            `Cannot duplicate document "${document.name || document.originalName || document.id || sourceBlobId}": `
                            + `its only browser-local blob ${sourceBlobId} is unavailable.`,
                        );
                        error.code = 'DUPLICATE_LOCAL_MEDIA_UNAVAILABLE';
                        error.mediaKind = 'document';
                        error.blobId = sourceBlobId;
                        throw error;
                    }
                    delete next.docBlobId;
                }
            }
            if (typeof next.pdfDataUrl === 'string' && next.pdfDataUrl.startsWith('blob:')) {
                delete next.pdfDataUrl;
            }
            out.push(next);
        }
        return out;
    } catch (error) {
        // The caller must never receive a half-cloned duplicate. Best-effort
        // removal here is backed by the project-wide rollback in App.jsx.
        await Promise.allSettled(createdTargetBlobIds.map((blobId) => (
            (db as any).document_blobs.delete(blobId)
        )));
        throw error;
    }
}

/** Get a displayable object URL for a stored document (cached; revoke later). */
export async function getDocumentUrl(blobId: any, expectedProjectId: any = null) {
    if (!blobId) return null;
    const expectedProjectKey = expectedProjectId == null ? null : String(expectedProjectId);
    if (expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey)) return null;
    const cacheKey = activeDocumentUrlKey(blobId, expectedProjectId);
    const cached = activeUrls.get(cacheKey);
    if (cached) {
        if (cached.projectId != null && deletedProjectIds.has(cached.projectId)) return null;
        if (expectedProjectKey != null
            && String(cached.projectId ?? '') !== expectedProjectKey) return null;
        return cached.url;
    }
    const pending = pendingUrlLoads.get(cacheKey);
    if (pending) return pending;

    const load = (async (): Promise<string | null> => {
        // Read the owner and bytes together so deletion can be checked again
        // after the await. Calling getDocumentBlob() here would discard the
        // project id and reopen the exact read/delete race this guard closes.
        const record = await (db as any).document_blobs.get(blobId);
        if (!record?.blob) return null;
        if (expectedProjectKey != null
            && String(record.projectId ?? '') !== expectedProjectKey) {
            console.warn(`[document-blob-store] Refused cross-project URL ${blobId} (owner=${record.projectId}, requested=${expectedProjectId})`);
            return null;
        }
        const ownerProjectKey = record.projectId == null ? expectedProjectKey : String(record.projectId);
        if ((expectedProjectKey != null && deletedProjectIds.has(expectedProjectKey))
            || (ownerProjectKey != null && deletedProjectIds.has(ownerProjectKey))) {
            return null;
        }
        const url = URL.createObjectURL(record.blob);
        activeUrls.set(cacheKey, { url, projectId: ownerProjectKey });
        return url;
    })();
    pendingUrlLoads.set(cacheKey, load);
    try {
        return await load;
    } finally {
        if (pendingUrlLoads.get(cacheKey) === load) pendingUrlLoads.delete(cacheKey);
    }
}

/** Revoke all active document object URLs (call on project switch / unload). */
export function revokeAllDocumentUrls() {
    const revoked = new Set<string>();
    for (const [, active] of activeUrls) {
        try { URL.revokeObjectURL(active.url); } catch { /* noop */ }
        revoked.add(active.url);
    }
    for (const url of sessionOwnersByUrl.keys()) {
        if (revoked.has(url)) continue;
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
    }
    activeUrls.clear();
    sessionOwnersByUrl.clear();
    sessionUrlsByProject.clear();
}

/**
 * Revoke only one project's preview URLs when the operator switches projects.
 * Other open project contexts are deliberately left alone; the project id is
 * recorded from the resolved row so this cannot revoke another project's doc.
 */
export function revokeProjectDocumentUrls(projectId: any): number {
    const owner = documentProjectKey(projectId);
    const projectKey = owner === PENDING_DOCUMENT_PROJECT ? null : String(projectId);
    const releaseCandidates = new Set<string>();
    for (const [cacheKey, active] of [...activeUrls.entries()]) {
        if (active.projectId !== projectKey) continue;
        releaseCandidates.add(active.url);
        activeUrls.delete(cacheKey);
    }
    const sessionUrls = sessionUrlsByProject.get(owner);
    if (sessionUrls) {
        for (const url of sessionUrls) {
            const owners = sessionOwnersByUrl.get(url);
            owners?.delete(owner);
            if (owners && owners.size > 0) continue;
            sessionOwnersByUrl.delete(url);
            releaseCandidates.add(url);
        }
        sessionUrlsByProject.delete(owner);
    }
    let revoked = 0;
    for (const url of releaseCandidates) {
        if ((sessionOwnersByUrl.get(url)?.size ?? 0) > 0) continue;
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        revoked += 1;
    }
    return revoked;
}

/** Read-only diagnostic seam used by focused resource-lifecycle tests. */
export function getActiveDocumentUrlCount(projectId: any = null): number {
    if (arguments.length === 0) {
        return new Set([
            ...[...activeUrls.values()].map(active => active.url),
            ...sessionOwnersByUrl.keys(),
        ]).size;
    }
    const owner = documentProjectKey(projectId);
    const projectKey = owner === PENDING_DOCUMENT_PROJECT ? null : String(projectId);
    const urls = new Set<string>(sessionUrlsByProject.get(owner) ?? []);
    for (const active of activeUrls.values()) {
        if (active.projectId === projectKey) urls.add(active.url);
    }
    return urls.size;
}

/** Delete all document blobs for a project (call when a project is deleted). */
export async function deleteProjectDocumentBlobs(projectId: any) {
    if (!projectId) return 0;
    // Set the tombstone before the first await and before revocation. Any read
    // already in flight observes it after resolving; any later read exits early.
    deletedProjectIds.add(String(projectId));
    revokeProjectDocumentUrls(projectId);
    const count = await (db as any).document_blobs.where('projectId').equals(projectId).count();
    await (db as any).document_blobs.where('projectId').equals(projectId).delete();
    return count;
}

/** Convert a base64 data URL to a Blob (manual decode, CSP-safe). */
export function dataUrlToBlob(dataUrl: any): Blob {
    const str = String(dataUrl);
    const commaIdx = str.indexOf(',');
    const header = commaIdx >= 0 ? str.slice(0, commaIdx) : '';
    const base64 = commaIdx >= 0 ? str.slice(commaIdx + 1) : '';
    const mimeMatch = header.match(/data:([^;]+)/);
    const mime = (mimeMatch && mimeMatch[1]) ? mimeMatch[1] : 'application/octet-stream';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

function isInlineBase64(v: any): boolean {
    return typeof v === 'string' && v.startsWith('data:') && v.includes(';base64,');
}

function isBlobUrl(v: any): boolean {
    return typeof v === 'string' && v.startsWith('blob:');
}

/** True only for bytes the current browser can render without cloud I/O. */
export function hasRenderableLocalDocumentPreview(doc: any): boolean {
    const source = doc?.pdfDataUrl;
    return isBlobUrl(source) || (isInlineBase64(source) && String(source).length > 200);
}

/**
 * True for an image document whose picture this browser holds inline in `data`
 * (a data:image URL or a live blob: URL). Such a document previews from that
 * copy, not from its cloud reference, the same local-first rule the PDF
 * preview follows with `pdfDataUrl`.
 */
export function hasRenderableLocalImageDocument(doc: any): boolean {
    if (doc?.isImageDoc !== true) return false;
    const picture = doc?.data;
    return isBlobUrl(picture) || (isInlineBase64(picture) && /^data:image\//i.test(picture));
}

function binaryValueToBlob(value: any, mimeType: any): Blob | null {
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
    if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
        return new Blob([value], { type: mimeType || 'application/octet-stream' });
    }
    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value)) {
        // Copy into an ordinary ArrayBuffer. TypeScript correctly models a
        // generic view's backing store as ArrayBufferLike (which can include a
        // SharedArrayBuffer), while BlobPart only accepts a non-shared buffer.
        const source = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
        const bytes = new Uint8Array(source.byteLength);
        bytes.set(source);
        return new Blob([bytes.buffer], { type: mimeType || 'application/octet-stream' });
    }
    if (isInlineBase64(value)) return dataUrlToBlob(value);
    return null;
}

function stripTransientDocumentFields(doc: any) {
    if (!doc || typeof doc !== 'object') return doc;
    const {
        bytes: _bytes,
        file: _file,
        blob: _blob,
        handle: _handle,
        pdfBlob: _pdfBlob,
        pdfFile: _pdfFile,
        docBytes: _docBytes,
        arrayBuffer: _arrayBuffer,
        fileHandle: _fileHandle,
        directoryHandle: _directoryHandle,
        ...rest
    } = doc;
    const inlineData = isInlineBase64(rest.data)
        || (typeof ArrayBuffer !== 'undefined' && (rest.data instanceof ArrayBuffer || ArrayBuffer.isView(rest.data)));
    if (inlineData) delete rest.data;
    if (isBlobUrl(rest.pdfDataUrl) || isInlineBase64(rest.pdfDataUrl)) delete rest.pdfDataUrl;
    return rest;
}

function transientDocumentBlob(doc: any): Blob | null {
    const mimeType = doc?.mimeType
        || doc?.contentType
        || (String(doc?.name || '').toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
    for (const candidate of [
        doc?.file,
        doc?.blob,
        doc?.pdfBlob,
        doc?.pdfFile,
        doc?.bytes,
        doc?.docBytes,
        doc?.arrayBuffer,
        doc?.data,
        doc?.pdfDataUrl,
    ]) {
        const blob = binaryValueToBlob(candidate, mimeType);
        if (blob) return blob;
    }
    return null;
}

/**
 * Resolve bytes that can safely repair a document's stale cloud receipt.
 * Transient in-memory bytes win, then the project-owned IndexedDB blob. Cloud
 * aliases are deliberately ignored: FILE_ORPHAN means they are locators whose
 * durability has already failed, not a reason to suppress the local fallback.
 */
export async function resolveDocumentReuploadBlob(doc: any, projectId: any): Promise<Blob | null> {
    if (!doc || typeof doc !== 'object') return null;
    const transientBlob = transientDocumentBlob(doc);
    if (transientBlob) return transientBlob;
    if (!doc.docBlobId) return null;
    return getDocumentBlob(doc.docBlobId, projectId);
}

/**
 * Migrate a project's un-backed inline document bytes into the blob store.
 * Idempotent — a doc that already has a `docBlobId`, an S3 key, or a
 * fileMetadataId is left alone (all transient browser-only fields are dropped
 * so the save payload stays small). PDFs/DOCX/other documents carrying File,
 * Blob, ArrayBuffer, Uint8Array, `docBytes`, `data`, or `pdfDataUrl` bytes are
 * stored and stamped with `docBlobId`. Call just before save.
 *
 * @returns {Promise<{documents: any[], migrated: number}>}
 */
export async function migrateProjectDocuments(projectId: any, documents: any) {
    if (!Array.isArray(documents) || documents.length === 0) {
        return { documents: documents || [], migrated: 0 };
    }
    let migrated = 0;
    const out: any[] = [];
    for (const original of documents) {
        if (!original || typeof original !== 'object') { out.push(original); continue; }

        // EVID: estimator notes as written (stage1-estimator-notes.ts
        // rawEstimatorNotesBlob) get their own local copy, so they survive a
        // reload until data-service-api uploads them to the brain-only lane.
        // A failed store keeps the Blob on the row for the next save.
        let doc = original;
        if (typeof Blob !== 'undefined' && doc.rawNotesBlob instanceof Blob) {
            if (doc.rawNotesBlobId || doc.rawNotesS3Key) {
                const { rawNotesBlob: _rawNotesBlob, ...rest } = doc;
                doc = rest;
            } else {
                try {
                    const rawNotesBlobId = await storeDocumentBlob(doc.rawNotesBlob, projectId, {
                        name: doc.name,
                        mimeType: 'text/plain',
                    });
                    const { rawNotesBlob: _rawNotesBlob, ...rest } = doc;
                    doc = { ...rest, rawNotesBlobId };
                    migrated++;
                } catch (err: any) {
                    console.warn(`[document-blob-store] raw notes store failed for "${doc.name || 'unknown'}":`, err?.message);
                }
            }
        }

        // Already durably backed → keep, just make sure heavy inline bytes are
        // gone (they'll be rehydrated from docBlobId / S3 on load).
        if (doc.docBlobId || doc.pdfS3Key || doc.fileMetadataId) {
            out.push(stripTransientDocumentFields(doc));
            continue;
        }

        // Un-backed transient bytes → move to the local blob store before any
        // backend JSON save.  In particular, Uint8Array serialises as a huge
        // object with numeric keys; it must never cross the wire.
        const transientBlob = transientDocumentBlob(doc);
        if (transientBlob) {
            try {
                const docBlobId = await storeDocumentBlob(transientBlob, projectId, {
                    name: doc.name,
                    mimeType: transientBlob.type || doc.mimeType || doc.contentType || 'application/octet-stream',
                });
                out.push({ ...stripTransientDocumentFields(doc), docBlobId });
                migrated++;
                continue;
            } catch (err: any) {
                console.warn(`[document-blob-store] transient-byte migrate failed for "${doc.name || 'unknown'}":`, err?.message);
                out.push(doc);
                continue;
            }
        }

        out.push(doc);
    }
    if (migrated > 0) {
        console.log(`[document-blob-store] Migrated ${migrated}/${documents.length} document(s) to the blob store for project ${projectId}`);
    }
    return { documents: out, migrated };
}

/**
 * Rehydrate previews on load: for any doc that has a `docBlobId` but no live
 * inline preview, rebuild a `pdfDataUrl` object URL from the stored blob so the
 * Cockpit renders/downloads it again. Best-effort per doc; a missing blob just
 * leaves the row without a preview (the metadata still shows). Idempotent.
 *
 * @returns {Promise<{documents: any[], rehydrated: number}>} documents with
 *          preview URLs restored + how many were rebuilt (0 ⇒ nothing changed)
 */
export async function rehydrateProjectDocuments(projectId: any, documents: any) {
    if (!Array.isArray(documents) || documents.length === 0) {
        return { documents: documents || [], rehydrated: 0 };
    }
    let rehydrated = 0;
    const out: any[] = [];
    for (const doc of documents) {
        if (!doc || typeof doc !== 'object' || !doc.docBlobId) { out.push(doc); continue; }
        // Only PDFs render from an inline `pdfDataUrl` in the cockpit — DOCX/other
        // preview from their extracted `content` text, and their docBlobId is kept
        // as a download source. So only rebuild pdfDataUrl for PDF docs.
        const isPdfDoc = String(doc.ext || '').toLowerCase() === 'pdf'
            || String(doc.mimeType || doc.contentType || '').toLowerCase().includes('pdf')
            || String(doc.name || '').toLowerCase().endsWith('.pdf');
        if (!isPdfDoc) { out.push(doc); continue; }
        // Only an actually renderable local source suppresses rehydration. An
        // S3 key or metadata id is a locator, not proof that the object remains
        // available; retaining both aliases while materialising docBlobId lets
        // every stage fall back locally after FILE_ORPHAN.
        const hasLiveLocalPreview = hasRenderableLocalDocumentPreview(doc);
        if (hasLiveLocalPreview) { out.push(doc); continue; }
        try {
            const url = await getDocumentUrl(doc.docBlobId, projectId);
            if (url) { out.push({ ...doc, pdfDataUrl: url }); rehydrated++; }
            else out.push(doc);
        } catch (err: any) {
            console.warn(`[document-blob-store] rehydrate failed for "${doc.name || 'unknown'}":`, err?.message);
            out.push(doc);
        }
    }
    return { documents: out, rehydrated };
}
